import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AppNav from '../components/AppNav';
import { apiClient } from '../api';
import { Bell, Heart, Sparkles, Eye, MessageCircle, Star, Gift, Trash2, Check } from 'lucide-react';
import { Button } from '../components/ui/button';

const KIND_META = {
  like:            { Icon: Heart,         color: 'text-fnl-pink',    verb: 'liked your profile' },
  match:           { Icon: Sparkles,      color: 'text-fuchsia-400', verb: "matched with you" },
  super_like:      { Icon: Star,          color: 'text-amber-400',   verb: 'super-liked you' },
  message:         { Icon: MessageCircle, color: 'text-sky-400',     verb: 'sent you a message' },
  view:            { Icon: Eye,           color: 'text-purple-300',  verb: 'viewed your profile' },
  referral_credit: { Icon: Gift,          color: 'text-emerald-400', verb: 'joined via your invite' },
  ai_match:        { Icon: Sparkles,      color: 'text-fnl-pink',    verb: 'is your match of the day' },
};

const targetFor = (n) => {
  if (n.kind === 'message') return n.actor_id ? `/chat/${n.actor_id}` : '/chat';
  if (n.kind === 'view') return '/views';
  if (n.kind === 'referral_credit') return '/me';
  if (n.kind === 'ai_match') return '/browse';
  return n.actor_id ? `/profile/${n.actor_id}` : '/browse';
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  if (s < 604800) return `${Math.floor(s / 86400)} days ago`;
  return d.toLocaleDateString();
};

const NotificationsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);

  const load = useCallback(async (before = null) => {
    setLoading(true);
    try {
      const params = { limit: 30 };
      if (before) params.before = before;
      const r = await apiClient.get('/notifications', { params });
      const list = r.data || [];
      setItems((prev) => (before ? [...prev, ...list] : list));
      setHasMore(list.length >= 30);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    // Mark them all read on entry so the badge clears
    apiClient.post('/notifications/mark-read', {}).catch(() => {});
  }, [load]);

  const onDelete = async (id) => {
    setItems((prev) => prev.filter((n) => n.id !== id));
    try {
      await apiClient.delete(`/notifications/${id}`);
    } catch (e) { /* silent */ }
  };

  const onClearAll = async () => {
    setItems([]);
    setHasMore(false);
    try {
      await apiClient.delete('/notifications');
    } catch (e) { /* silent */ }
  };

  const onLoadMore = () => {
    const last = items[items.length - 1];
    if (last?.created_at) load(last.created_at);
  };

  return (
    <div className="min-h-screen bg-fnl-dark">
      <AppNav />
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
              <Bell className="w-7 h-7 text-fnl-pink" />Notifications
            </h1>
            <p className="text-purple-300 mt-1">Likes, matches, messages, and AI picks — all in one feed.</p>
          </div>
          {items.length > 0 && (
            <Button
              variant="outline"
              onClick={onClearAll}
              className="bg-fnl-purple-light/40 border-purple-700 text-white hover:bg-fnl-purple-light hover:text-white"
              data-testid="notifications-clear-all"
            >
              <Trash2 className="w-4 h-4 mr-2" />Clear all
            </Button>
          )}
        </div>

        {loading && items.length === 0 ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-fnl-pink border-t-transparent rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20" data-testid="notifications-page-empty">
            <Bell className="w-16 h-16 text-fnl-pink/30 mx-auto mb-4" />
            <p className="text-white text-xl font-semibold mb-1">No notifications yet</p>
            <p className="text-purple-300">Likes, matches, and messages will show up here.</p>
            <Link to="/browse" className="inline-block mt-6">
              <Button className="bg-fnl-pink hover:bg-fnl-pink-light text-white">Start browsing</Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="space-y-2" data-testid="notifications-page-list">
              {items.map((n) => {
                const meta = KIND_META[n.kind] || { Icon: Bell, color: 'text-purple-300', verb: n.kind };
                const Icon = meta.Icon;
                return (
                  <div
                    key={n.id}
                    className={`group flex items-start gap-3 px-4 py-3 rounded-xl border ${n.read ? 'bg-fnl-purple/40 border-purple-900/40' : 'bg-fnl-pink/8 border-fnl-pink/30'}`}
                    data-testid={`notif-page-${n.id}`}
                  >
                    <Link to={targetFor(n)} className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="relative shrink-0">
                        {n.actor_photo ? (
                          <img src={n.actor_photo} alt={n.actor_name || ''} className="w-12 h-12 rounded-full object-cover border border-purple-800/60" />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-fnl-purple-light/60 flex items-center justify-center">
                            <Bell className="w-5 h-5 text-purple-300" />
                          </div>
                        )}
                        <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-fnl-dark border-2 border-fnl-purple flex items-center justify-center ${meta.color}`}>
                          <Icon className="w-3 h-3" />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-white leading-snug">
                          <span className="font-semibold">{n.actor_name || 'Someone'}</span>{' '}
                          <span className="text-purple-200">{meta.verb}</span>
                        </div>
                        {n.kind === 'message' && n.payload?.preview && (
                          <div className="text-sm text-purple-300 truncate mt-0.5">"{n.payload.preview}"</div>
                        )}
                        {n.kind === 'ai_match' && n.payload?.reason && (
                          <div className="text-sm text-purple-300 mt-0.5 italic">{n.payload.reason}</div>
                        )}
                        <div className="text-[11px] text-purple-400 mt-1 uppercase tracking-wider font-semibold flex items-center gap-2">
                          {timeAgo(n.created_at)}
                          {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-fnl-pink inline-block" />}
                        </div>
                      </div>
                    </Link>
                    <button
                      type="button"
                      onClick={() => onDelete(n.id)}
                      aria-label="Delete notification"
                      className="text-purple-400 hover:text-fnl-pink opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                      data-testid={`notif-delete-${n.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
            {hasMore && items.length >= 30 && (
              <div className="text-center mt-6">
                <Button
                  variant="ghost"
                  onClick={onLoadMore}
                  className="text-fnl-pink hover:bg-fnl-pink/10 hover:text-fnl-pink"
                  data-testid="notifications-load-more"
                >
                  <Check className="w-4 h-4 mr-2" />Load more
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
